import { cn } from '@/lib/utils'

export type SummaryTone = 'neutral' | 'positive' | 'negative' | 'warning'

export interface SummaryMetric {
  label: string
  value: string
  tone?: SummaryTone
  /** Texto pequeno embaixo do valor (ex.: "X fretes", "{N} faturas"). */
  hint?: string
}

interface ListSummaryProps {
  readonly metrics: readonly SummaryMetric[]
  readonly className?: string
  /**
   * Texto pequeno no canto superior direito (default: "Totais do filtro atual").
   * Use `null` para esconder.
   */
  readonly caption?: string | null
}

const TONE_TEXT: Record<SummaryTone, string> = {
  neutral: 'text-foreground',
  positive: 'text-green-700',
  negative: 'text-red-700',
  warning: 'text-amber-700',
}

/**
 * Faixa de cards-resumo para topo de listas. Os números refletem o
 * **filtro atual** (não só a página visível) — o backend agrega via SQL.
 * Se nenhuma métrica vier preenchida, não renderiza.
 */
export function ListSummary({
  metrics,
  className,
  caption = 'Totais do filtro atual',
}: ListSummaryProps) {
  if (metrics.length === 0) return null

  return (
    <div className={cn('space-y-2', className)}>
      {caption && (
        <p className="text-xs text-muted-foreground">{caption}</p>
      )}
      <div
        className={cn(
          'grid gap-3',
          metrics.length === 2 ? 'grid-cols-2' : 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3',
        )}
      >
        {metrics.map((metric) => (
          <div key={metric.label} className="rounded-lg border border-border bg-card p-4">
            <p className="text-xs font-medium text-muted-foreground">{metric.label}</p>
            <p
              className={cn(
                'mt-2 text-xl font-semibold tabular-nums',
                TONE_TEXT[metric.tone ?? 'neutral'],
              )}
            >
              {metric.value}
            </p>
            {metric.hint && <p className="mt-1 text-xs text-muted-foreground">{metric.hint}</p>}
          </div>
        ))}
      </div>
    </div>
  )
}
